import { apiClient } from "@/api/client";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EmailRecipient } from "@/user/user";

export type NotificationPreferences = {
  daily_summary: boolean;
  weekly_summary: boolean;
  critical_alerts: boolean;
};

export type NotificationPreferenceFeedback = {
  preference_field: string;
  previous_value: boolean;
  new_value: boolean;
  reason: string;
  comment?: string;
};

export const notificationKeys = createQueryKeys("notifications", {
  preferences: () => ({
    queryKey: ["preferences"],
    queryFn: async () => {
      const res = await apiClient.get<NotificationPreferences>(
        "notifications/preferences/"
      );
      return res.data;
    },
  }),
});

export const useNotificationPreferences = () => {
  return useQuery(notificationKeys.preferences());
};

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (update: Partial<NotificationPreferences>) => {
      const res = await apiClient.patch<NotificationPreferences>(
        "notifications/preferences/",
        update
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        notificationKeys.preferences().queryKey,
        data
      );
    },
  });
};

export const useSubmitPreferenceFeedback = () => {
  return useMutation({
    mutationFn: async (data: NotificationPreferenceFeedback) => {
      const res = await apiClient.post(
        "notifications/preferences/feedback/",
        data
      );
      return res.data;
    },
  });
};

export const useSearchUsers = (query: string) => {
  return useQuery({
    queryKey: ["notifications", "user-search", query],
    queryFn: async () => {
      const res = await apiClient.get<EmailRecipient[]>(
        `notifications/users/search/?q=${encodeURIComponent(query)}`
      );
      return res.data;
    },
    enabled: query.length >= 2,
  });
};
